/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { cn, tailwindUtils } from '@/shared/ui/utils/tailwind';
import { UnstyledButton } from '@/shared/ui';

export interface CreateLeaseSubmitButtonProps {
  isSubmitting: boolean;
  onSubmit: () => void;
  /** Overrides for reuse outside the create form; default to its copy. */
  label?: string;
  savingLabel?: string;
  testId?: string;
}

/**
 * Footer submit button for the create-lease drawer.
 *
 * @param props - Submission state and the submit handler.
 * @returns The drawer's submit control.
 */
export function CreateLeaseSubmitButton({
  isSubmitting,
  onSubmit,
  label,
  savingLabel,
  testId = 'create-lease-submit',
}: CreateLeaseSubmitButtonProps) {
  const { t } = useTranslation('features/leases');
  const idleLabel = label ?? t('create.submit');

  return (
    <UnstyledButton
      type="button"
      onClick={onSubmit}
      disabled={isSubmitting}
      aria-label={idleLabel}
      data-testid={testId}
      className={cn(
        tailwindUtils.buttonBase,
        'h-10 min-w-[7.5rem] px-(--space-5)',
        'bg-(--button-primary-bg) text-(--button-primary-text)',
        'hover:bg-(--button-primary-hover-bg) hover:shadow-[var(--shadow-md)]',
        'text-[length:var(--text-body)]',
        'font-[weight:var(--font-semibold)]',
        'leading-[var(--leading-snug)]',
        'font-[family-name:var(--font-family)]',
        'disabled:cursor-not-allowed disabled:opacity-60',
      )}
    >
      {isSubmitting ? (savingLabel ?? t('create.saving')) : idleLabel}
    </UnstyledButton>
  );
}
